import { useEffect, useRef, useState } from 'react'
import { useClickAnyWhere, useEventListener, useOnClickOutside } from 'usehooks-ts'

import DynamicAction from '@/components/ui/dynamic-action'
import HeaderCard from '@/components/header-card'
import { buttonStyles } from '@/components/ui/cta-link'
import colors from '@/constants/colors'
import { cx } from '@/utils/strings.ts'
import { TypeHeaderRichMenu } from '@/types/ctf'
import Newsletter from './newsletter'

type HeaderRichMenuProps = TypeHeaderRichMenu<'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] & {
  className?: string
  onLinkClick?: () => void
  /** Background color of parent header */
  headerBackgroundColor?: string
  /** Newsletter Form entry */
  newsletterForm?: any
}

type HeaderRichMenuContentProps = {
  cards: HeaderRichMenuProps['cards']
  links: HeaderRichMenuProps['links']
  backgroundColor?: HeaderRichMenuProps['backgroundColor']
  includeNewsletter: HeaderRichMenuProps['includeNewsletter']
  newsletterForm?: any
  isCondensed: boolean
  onLinkClick?: () => void
  menuOpen?: boolean
}

export function HeaderRichMenuContent({
  cards,
  links,
  includeNewsletter,
  newsletterForm,
  isCondensed,
  onLinkClick,
  backgroundColor,
  menuOpen,
}: HeaderRichMenuContentProps) {
  const cardGroups =
    cards?.reduce(
      (acc, card) => {
        if (!card) return acc
        if (card.fields.links && card.fields.links.length > 1) {
          acc.push({ cards: [card], count: card.fields.links.length })
        } else {
          const lastGroup = acc[acc.length - 1]
          if (lastGroup && lastGroup.count < 2) {
            lastGroup.cards?.push(card)
            lastGroup.count += 1
          } else {
            acc.push({ cards: [card], count: 1 })
          }
        }
        return acc
      },
      [] as { cards: HeaderRichMenuProps['cards']; count: number }[]
    ) || []

  return (
    <>
      {cardGroups.length > 0 && (
        <div className={cx('grid max-w-full gap-3 md:gap-0', !isCondensed ? `grid-flow-col` : `grid-cols-2`)}>
          {cardGroups.map((group, groupIndex) => {
            const allCardsHaveSubtext = group.cards?.every((card) => card?.fields.subtext)
            const allCardsDontHaveSubtext = group.cards?.every((card) => !card?.fields.subtext)
            return group.cards?.map(
              (card) =>
                card && (
                  <HeaderCard
                    key={card.sys.id}
                    className={cx(
                      `rounded-lg md:rounded-none gap-0`,
                      // Only enable subgrid if all cards a consistent layout
                      !allCardsHaveSubtext && !allCardsDontHaveSubtext && 'grid grid-rows-subgrid',
                      (group.cards?.length || 0) > 1
                        ? `col-span-2 *:col-span-2 md:col-span-1 md:*:col-auto!`
                        : `col-span-2`,
                      !isCondensed ? `row-span-3 p-8` : `grid-cols-subgrid p-4`,
                      group.cards?.length === 1 && isCondensed && `*:col-span-2`,
                      card.fields.backgroundColor === 'White' && `bg-white dark:bg-secondary-700`
                    )}
                    onLinkClick={onLinkClick}
                    {...card.fields}
                    menuOpen={menuOpen}
                  />
                )
            )
          })}
        </div>
      )}

      {includeNewsletter && newsletterForm && menuOpen && (
        <div
          className={cx(
            'px-6 text-secondary-800 dark:text-secondary-50 min-h-[72px] bg-secondary-100 dark:bg-secondary-700',
            isCondensed ? 'mt-4' : 'mt-3',
            '[&_label]:dark:!bg-secondary-700 [&_label]:text-secondary-600'
          )}
        >
          <Newsletter form={newsletterForm.fields} />
        </div>
      )}

      {links && links.length > 0 && (
        <div
          className={cx(
            `w-full mt-4 flex flex-col gap-4`,
            `md:flex-row md:items-center md:gap-x-6 md:gap-y-4 md:px-12`,
            `lg:py-4 lg:px-10 lg:bg-white lg:flex-row lg:flex-wrap`,
            backgroundColor === colors.white && 'lg:bg-secondary-50',
            isCondensed && links.length > 3 && `md:grid md:grid-cols-2`,
            'dark:[&_a]:text-primaryBlue'
          )}
        >
          {links.map(
            (link) =>
              link && (
                <DynamicAction
                  key={link.sys.id}
                  className={cx(
                    `no-underline transition-colors`,
                    (link.fields.style === buttonStyles.btnPlain || link.fields.style === buttonStyles.btnArrow) &&
                      `py-0!`,
                    link.fields.style === buttonStyles.btnSolid && `bg-primaryBlue!`
                  )}
                  onClick={onLinkClick}
                  {...(link.fields as any)}
                  size="Smaller"
                />
              )
          )}
        </div>
      )}
    </>
  )
}

export default function HeaderRichMenu({
  internalName,
  className,
  title,
  cards,
  links,
  includeNewsletter = false,
  newsletterForm,
  onLinkClick,
  backgroundColor,
  headerBackgroundColor = colors.white,
  shouldAlwaysBeFullWidth = false,
}: HeaderRichMenuProps) {
  const [responsive, setResponsive] = useState({ screenWidth: 0, bottom: 0, left: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)

  const onResize = () => {
    if (buttonRef.current) {
      const box = buttonRef.current.getBoundingClientRect()
      setResponsive({ screenWidth: window.innerWidth, bottom: box.bottom, left: box.left })
    }
  }
  useEventListener('resize', onResize)
  useEffect(() => {
    onResize()
  }, [])

  const isCondensed = responsive.screenWidth < (shouldAlwaysBeFullWidth ? 1024 : 1314)
  const shouldAlignRight = isCondensed && responsive.left > 0 && responsive.left + 680 > responsive.screenWidth

  const containerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  useOnClickOutside(containerRef, () => {
    setOpen(false)
  })

  // Hide the outline only when the user clicks on the button
  const [hideOutline, setHideOutline] = useState(true)
  useClickAnyWhere((e) => {
    setHideOutline(true)
  })
  useEventListener('keydown', () => {
    setHideOutline(false)
  })

  return (
    <div className={className} ref={containerRef}>
      <button
        ref={buttonRef}
        className={cx(
          'relative flex items-center gap-1 py-3 pr-1',
          headerBackgroundColor === colors.white && 'hover:text-indigoBlue',
          'dark:hover:text-primaryBlue',
          hideOutline && `outline-hidden`
        )}
        onClick={() => {
          setHideOutline(true)
          setOpen(!open)
        }}
      >
        <span className="text-base">{title}</span>
        <span className={cx('mt-[1px] ml-[1px] font-bwi text-base duration-150', open ? '-rotate-180' : 'rotate-0')}>
          &#xe92d;
        </span>
        {/* Animated border on open */}
        <div
          className={cx(
            'absolute bottom-2 left-0 w-full h-[2px] bg-[#487FE2] dark:bg-primaryBlue',
            'ease-out] transition-transform duration-300 scale-x-0 origin-left',
            open && 'scale-x-100'
          )}
        ></div>
      </button>

      <div
        className={cx(
          'absolute top-full left-5 translate-y-[1px] transition-opacity',
          !isCondensed ? `w-[calc(100% - 2.5rem)]` : `w-[680px]`,
          !open && 'pointer-events-none',
          open ? 'opacity-100 duration-[300ms] ease-out]' : 'opacity-0 duration-[150ms] ease-in'
        )}
        style={
          !shouldAlignRight
            ? {
                left: !isCondensed ? '1.25rem' : responsive.left,
              }
            : {
                left: 'auto',
                right: '1.25rem',
              }
        }
        aria-hidden={!open}
      >
        <div
          key={internalName || `header-${title}`}
          className={cx(
            'rounded-b-lg border-x-[1px] border-b-[1px] border-secondary-100 dark:border-secondary-700 transform-gpu overflow-hidden',
            !isCondensed && 'w-[1440px] max-w-[calc(100vw-56px)]',
            isCondensed && 'pt-4',
            (!backgroundColor || backgroundColor === colors.lightGray) && 'bg-secondary-50',
            backgroundColor === colors.white && 'bg-white',
            'dark:bg-secondary-800 origin-top transition-transform ease-out]',
            open ? 'scale-y-100 duration-[350ms] ease-out]' : 'scale-y-50 duration-[200ms] ease-in'
          )}
        >
          <div
            className={cx(
              'transition-transform',
              open ? 'scale-y-100 duration-[350ms] ease-out]' : 'scale-y-150 duration-[200ms]  ease-in'
            )}
          >
            <HeaderRichMenuContent
              cards={cards}
              links={links}
              includeNewsletter={includeNewsletter}
              newsletterForm={newsletterForm}
              isCondensed={isCondensed}
              onLinkClick={onLinkClick}
              backgroundColor={backgroundColor}
              menuOpen={open}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
